import { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { FormInput } from '../../../components/input/index';
import { useReactForm } from '../../../hooks/index';
import { Scenario, ScenarioType } from '../../../types/scenario-modeling.types';
import { generateScenarioDefaultName } from '../../../utils/generate-scenario-default-name.util';
import { FormSchema, formSchema } from './modals.validation';

export type SaveModalProps = {
  isPending?: boolean;
  onClose: () => void;
  onSuccess: (name: string | undefined) => void;
  scenarioNames?: string[];
  scenarios?: Scenario[];
  type: ScenarioType;
};

export const SaveModal: FC<SaveModalProps> = ({
  onClose,
  onSuccess,
  scenarioNames,
  isPending,
  type,
  scenarios,
}) => {
  const scenarioTypeLength = scenarios?.filter((scenario) => scenario.type === type).length || 0;
  const defaultName = generateScenarioDefaultName(type, scenarioTypeLength);
  const { control } = useReactForm({
    schema: formSchema,
    defaultValues: { name: defaultName },
  });
  const { name } = useWatch<FormSchema>({ control });
  const { success } = formSchema.safeParse({ name });

  const trimName = name?.trim().toLocaleLowerCase();
  const isNameDuplicates = trimName ? scenarioNames?.includes(trimName) : false;

  const isValid = success && !isNameDuplicates;

  const handleSave = () => {
    onSuccess(name?.trim());
  };

  return (
    <div className="m-2 flex w-full max-w-[493px] flex-col gap-8 rounded bg-white p-4">
      <div className="flex flex-col gap-4">
        <span className="text-xl font-semibold text-gray-700">
          <AppFormattedMessage id="SAVE" /> <AppFormattedMessage id="SCENARIO" />
        </span>
        <FormInput
          control={control}
          errorMessage={
            isNameDuplicates && <AppFormattedMessage id="SCENARIO_NAME_ALREADY_EXISTS" />
          }
          name={'name'}
          placeholder={<AppFormattedMessage id="SCENARIO_NAME" />}
          wrapperClassName={'transition-all duration-1000 w-full'}
        />
      </div>
      <div className="flex h-11 w-full justify-end gap-3">
        <Button
          className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
          onClick={onClose}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="h-full w-fit bg-brand-700 px-6 py-[10px] text-sm font-[550] text-white"
          disabled={!isValid}
          isLoading={isPending}
          onClick={handleSave}
          styleType="NONE"
        >
          <AppFormattedMessage id="SAVE" />
        </Button>
      </div>
    </div>
  );
};
